import type { GraphicsSignals } from "../types";
import { hashString, hashValue } from "../utils/hash";
import { safe } from "../utils/safe";

function getGl(): WebGLRenderingContext | null {
  return safe(() => {
    const canvas = document.createElement("canvas");
    return (canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
  }, null);
}

/**
 * WebGL signals. The headline is the *unmasked* renderer string (via
 * WEBGL_debug_renderer_info): `SwiftShader` / `llvmpipe` / `Mesa` ⇒ software
 * rendering, i.e. an emulator or datacenter VM. Strongest single bot signal per
 * the formv3 teardown — ThumbmarkJS only exposes a hash, so we collect it raw.
 */
export function collectWebgl(): Pick<
  GraphicsSignals,
  | "webglVendor"
  | "webglRenderer"
  | "webglUnmaskedVendor"
  | "webglUnmaskedRenderer"
  | "webglReportHash"
  | "webglImageHash"
  | "webglExtHash"
> {
  const gl = getGl();
  if (!gl) {
    return {
      webglVendor: null,
      webglRenderer: null,
      webglUnmaskedVendor: null,
      webglUnmaskedRenderer: null,
      webglReportHash: null,
      webglImageHash: null,
      webglExtHash: null,
    };
  }

  const vendor = safe(() => String(gl.getParameter(gl.VENDOR)), null);
  const renderer = safe(() => String(gl.getParameter(gl.RENDERER)), null);

  const dbg = safe(() => gl.getExtension("WEBGL_debug_renderer_info"), null);
  const unmaskedVendor = dbg
    ? safe(() => String(gl.getParameter((dbg as any).UNMASKED_VENDOR_WEBGL)), null)
    : null;
  const unmaskedRenderer = dbg
    ? safe(() => String(gl.getParameter((dbg as any).UNMASKED_RENDERER_WEBGL)), null)
    : null;

  // Report hash: stable digest of a broad set of GL parameters/capabilities.
  const report = safe(() => {
    const params: Record<string, unknown> = { vendor, renderer, unmaskedVendor, unmaskedRenderer };
    const names = [
      "MAX_TEXTURE_SIZE",
      "MAX_VIEWPORT_DIMS",
      "MAX_RENDERBUFFER_SIZE",
      "MAX_VERTEX_ATTRIBS",
      "MAX_VERTEX_UNIFORM_VECTORS",
      "MAX_FRAGMENT_UNIFORM_VECTORS",
      "MAX_VARYING_VECTORS",
      "MAX_COMBINED_TEXTURE_IMAGE_UNITS",
      "MAX_TEXTURE_IMAGE_UNITS",
      "ALIASED_LINE_WIDTH_RANGE",
      "ALIASED_POINT_SIZE_RANGE",
      "RED_BITS",
      "GREEN_BITS",
      "BLUE_BITS",
      "ALPHA_BITS",
      "DEPTH_BITS",
      "STENCIL_BITS",
    ] as const;
    for (const n of names) {
      const v = (gl as any)[n];
      if (v !== undefined) params[n] = gl.getParameter(v);
    }
    const sl = gl.getShaderPrecisionFormat(gl.VERTEX_SHADER, gl.HIGH_FLOAT);
    if (sl) params["VERTEX_HIGH_FLOAT"] = [sl.rangeMin, sl.rangeMax, sl.precision];
    return params;
  }, null);

  const extensions = safe(() => gl.getSupportedExtensions() ?? [], []);

  return {
    webglVendor: vendor,
    webglRenderer: renderer,
    webglUnmaskedVendor: unmaskedVendor,
    webglUnmaskedRenderer: unmaskedRenderer,
    webglReportHash: report ? hashValue(report) : null,
    webglImageHash: hashString(renderImageProbe(gl)),
    webglExtHash: extensions.length ? hashValue([...extensions].sort()) : null,
  };
}

/** Render a small scene and read back pixels — GPU/driver-specific output. */
function renderImageProbe(gl: WebGLRenderingContext): string {
  return safe(() => {
    const vs = gl.createShader(gl.VERTEX_SHADER)!;
    gl.shaderSource(
      vs,
      "attribute vec2 p;void main(){gl_Position=vec4(p,0.0,1.0);gl_PointSize=64.0;}"
    );
    gl.compileShader(vs);

    const fs = gl.createShader(gl.FRAGMENT_SHADER)!;
    gl.shaderSource(
      fs,
      "precision mediump float;void main(){gl_FragColor=vec4(0.3,0.6,0.9,1.0);}"
    );
    gl.compileShader(fs);

    const prog = gl.createProgram()!;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 0.4, 0.7, -0.5, -0.6]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    const w = gl.drawingBufferWidth;
    const h = gl.drawingBufferHeight;
    const pixels = new Uint8Array(w * h * 4);
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    return Array.from(pixels.subarray(0, 1024)).join(",");
  }, "");
}
